import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Package, Clock, CheckCircle, Truck, XCircle } from "lucide-react";

const OrdersPage = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`);
        setOrders(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      } catch (error) {
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return <Clock className="w-5 h-5 text-yellow-500" />;
      case "paid": return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case "processing": return <Package className="w-5 h-5 text-sky-500" />;
      case "shipped": return <Truck className="w-5 h-5 text-purple-500" />;
      case "delivered": return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case "cancelled": return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "text-yellow-500 bg-yellow-500/10";
      case "paid": return "text-emerald-500 bg-emerald-500/10";
      case "processing": return "text-sky-500 bg-sky-500/10";
      case "shipped": return "text-purple-500 bg-purple-500/10";
      case "delivered": return "text-emerald-500 bg-emerald-500/10";
      case "cancelled": return "text-red-500 bg-red-500/10";
      default: return "text-slate-500 bg-slate-500/10";
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <Package className="w-8 h-8 text-yellow-500" />
            <h1 className="text-3xl font-black text-white font-['Outfit']">{t("orders.title")}</h1>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">{t("orders.empty")}</h2>
              <p className="text-slate-400 mb-6">Start shopping to see your orders here</p>
              <Link to="/store" className="btn-primary px-6 py-3 inline-block">
                Browse Store
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order.id} className="card-surface p-6" data-testid={`order-${order.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        {getStatusIcon(order.status)}
                        <span className={`text-xs font-mono uppercase px-2 py-1 ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm">
                        Order #{order.id.slice(0, 8)} • {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-white">R{order.total.toFixed(2)}</p>
                      <p className="text-slate-400 text-sm">{order.items.length} items</p>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-white/10">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <img 
                          src={item.product_image || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=100"}
                          alt={item.product_name}
                          className="w-12 h-12 object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{item.product_name}</p>
                          <p className="text-slate-400 text-xs">
                            Qty: {item.quantity}
                            {item.size && ` • Size: ${item.size}`}
                          </p>
                        </div>
                        <p className="text-white text-sm">R{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>

                  {order.shipping_address && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Shipping to</p>
                      <p className="text-slate-300 text-sm">
                        {order.shipping_address.name}, {order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.postalCode}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrdersPage;
